import CustomerContractSelect from '@/components/Form/Shared/CustomerContractSelect'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useModal } from '@/contexts/modal'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import {
    convertContractToSubcontract,
    getSubcontract,
    previewConvertToSubcontract,
} from '@/modules/Contract/modules/Subcontract/services'
import { UpdateSubcontractModalProps } from '@/modals/ContractModals/UpdateSubcontractModal'
import { CustomerContract } from '@/modules/Contract/types'
import { ModalProps } from '@/types/shared'
import { generatePathDatacentral, paths } from '@/utils/path'
import { useEffect, useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button, Dialog, GridHookForm, Typogaphy } from 'vnetwork-ui'

export interface ConvertContractToSubcontractModalProps extends ModalProps {
    contract: CustomerContract
    contractID: number
}

interface ConvertFormValues {
    source_contract_id: number
    reason: string
}

/**
 * Converts an existing customer contract into an annex (phụ lục) of this one.
 *
 * The flow is deliberately preview-then-confirm: the conversion is one-way for end users (there is
 * no undo in v1), so the user must see exactly which services, files and open processes move, and
 * every blocker, before the button unlocks.
 */
const ConvertContractToSubcontractModal = ({
    onClose,
    open,
    contract,
    contractID,
}: ConvertContractToSubcontractModalProps) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { setModal } = useModal()
    const { refetch } = useQueryClientCallback({
        pattern: 'contracts',
        mode: 'contains',
    })

    const formControl = useForm<ConvertFormValues>({
        defaultValues: { source_contract_id: 0, reason: '' },
    })
    const sourceContractID = useWatch({
        control: formControl.control,
        name: 'source_contract_id',
    })

    // Generated once per mounted modal so a retry after a network blip replays the original
    // conversion instead of creating a second annex.
    const idempotencyKey = useMemo(() => crypto.randomUUID(), [])
    const [previewError, setPreviewError] = useState<string | null>(null)

    const {
        mutateAsync: runPreview,
        data: preview,
        isPending: isPreviewing,
        reset: resetPreview,
    } = useMutationWithHandler(
        { mutationFn: previewConvertToSubcontract },
        { showSuccessToast: false }
    )

    // GridHookForm exposes no onChange hook, so watch the field directly: changing the source
    // contract invalidates whatever was previewed and must re-lock the confirm button.
    useEffect(() => {
        resetPreview()
        setPreviewError(null)
    }, [sourceContractID])

    const { mutateAsync: runConvert, isPending: isConverting } =
        useMutationWithHandler(
            { mutationFn: convertContractToSubcontract },
            {
                successMessage: t(
                    'contract:convert-contract-to-subcontract-success'
                ),
                reactQueryKeys: QUERY_KEYS.CONTRACTS,
                // Convert copies the source contract's terms verbatim — right by default, but the
                // annex usually needs a touch-up (dates, note, its own paperwork). Hand the user
                // straight to the edit form instead of making them find the annex and click Edit.
                // The form needs the whole annex, which the convert response does not carry, so it
                // is fetched here; if that fetch fails the old behaviour (navigate to the annex)
                // still applies, because losing the shortcut must never look like a failed convert.
                onSuccess: async (result: any) => {
                    onClose()
                    refetch()
                    if (!result?.subcontract_id) return

                    const goToAnnex = () =>
                        navigate(
                            generatePathDatacentral(paths.subContractDetail, {
                                contractID,
                                subcontractID: result.subcontract_id,
                            })
                        )

                    try {
                        const annex = await getSubcontract({
                            contractID,
                            subcontractID: result.subcontract_id,
                        })
                        if (!annex) {
                            goToAnnex()
                            return
                        }
                        setModal<UpdateSubcontractModalProps>(
                            'ContractModals/UpdateSubcontractModal/index',
                            {
                                contractID,
                                subcontractID: result.subcontract_id,
                                subcontract: annex,
                            }
                        )
                    } catch {
                        goToAnnex()
                    }
                },
            }
        )

    const handlePreview = async () => {
        setPreviewError(null)
        if (!sourceContractID) {
            setPreviewError(t('contract:convert-source-contract-required'))
            return
        }
        await runPreview({ contractID, sourceContractID })
    }

    const handleConvert = async (values: ConvertFormValues) => {
        if (!preview?.convertible) {
            setPreviewError(t('contract:convert-preview-required'))
            return
        }
        await runConvert({
            contractID,
            sourceContractID: values.source_contract_id,
            reason: values.reason,
            idempotencyKey,
        })
    }

    const busy = isPreviewing || isConverting

    return (
        <Dialog
            title={t('contract:convert-contract-to-subcontract')}
            onClose={onClose}
            open={open}
        >
            <GridHookForm
                className="pt-2"
                formControl={formControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onClose,
                        disabled: busy,
                    },
                    {
                        title: t(
                            'contract:convert-contract-to-subcontract-submit'
                        ),
                        type: 'submit',
                        variant: 'contained',
                        loading: isConverting,
                        disabled: busy || !preview?.convertible,
                    },
                ]}
                onSubmit={handleConvert}
                disabled={busy}
                data={[
                    {
                        renderEditComponent: (control) => (
                            <CustomerContractSelect
                                control={control}
                                name="source_contract_id"
                                placeholder={t(
                                    'contract:convert-source-contract'
                                )}
                                customer_id={Number(contract?.customer_id)}
                            />
                        ),
                        className: 'col-span-4',
                    },
                    {
                        renderEditComponent: () => (
                            <div className="flex flex-col gap-2">
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={handlePreview}
                                    disabled={busy || !sourceContractID}
                                >
                                    {t('contract:convert-preview')}
                                </Button>

                                {previewError && (
                                    <Typogaphy className="text-red-600 text-sm">
                                        {previewError}
                                    </Typogaphy>
                                )}

                                {preview && (
                                    <div className="rounded-md border p-3 space-y-1 text-sm">
                                        <Typogaphy className="font-medium">
                                            {t(
                                                'contract:convert-preview-summary',
                                                {
                                                    code: preview.subcontract_code_preview,
                                                    services:
                                                        preview.service_ids
                                                            ?.length ?? 0,
                                                    processes:
                                                        preview.active_process_count ??
                                                        0,
                                                }
                                            )}
                                        </Typogaphy>

                                        {preview.blockers?.map((blocker) => (
                                            <Typogaphy
                                                key={blocker}
                                                className="text-red-600"
                                            >
                                                {t(blocker)}
                                            </Typogaphy>
                                        ))}

                                        {preview.service_conflicts?.map(
                                            (conflict) => (
                                                <Typogaphy
                                                    key={`${conflict.service_id}-${conflict.reason}`}
                                                    className="text-red-600"
                                                >
                                                    {`#${conflict.service_id} — ${t(conflict.reason)}${
                                                        conflict.conflicting_contract_number_id
                                                            ? ` (${conflict.conflicting_contract_number_id})`
                                                            : ''
                                                    }${
                                                        conflict.conflicting_subcontract_code
                                                            ? ` (${conflict.conflicting_subcontract_code})`
                                                            : ''
                                                    }`}
                                                </Typogaphy>
                                            )
                                        )}
                                    </div>
                                )}

                                <Typogaphy className="text-amber-700 text-sm">
                                    {t(
                                        'contract:convert-contract-to-subcontract-warning'
                                    )}
                                </Typogaphy>
                            </div>
                        ),
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'reason',
                            type: 'textarea',
                            label: t('contract:convert-reason'),
                            placeholder: t('contract:convert-reason'),
                        },
                        className: 'col-span-4',
                    },
                ]}
            />
        </Dialog>
    )
}

export default ConvertContractToSubcontractModal
